import { ColumnDef } from '@tanstack/react-table';
import { differenceInCalendarDays, format } from 'date-fns';
import { Checkbox } from '@/components/ui/checkbox';
import BenefitTypeCell from '@/components/benefits/BenefitTypeCell';
import SupplierStatusCell from '@/components/suppliers/SupplierStatusCell';
import { SupplierStatus } from '../suppliers/SupplierStatus';
import { Translator } from '../translator.type';
import { formatBenefitCode, formatCircuitCode } from '@/utils/trash/formatCode';

type DeletedBy = {
  firstname: string | null;
  lastname: string | null;
  email: string;
} | null;

export type TrashedBenefit = {
  id: string;
  code: number | null;
  name: string | null;
  type: string;
  activity: { activityType: string } | null;
  supplier: { name: string } | null;
  deletedAt: string;
  deletedBy: DeletedBy;
  purgeAt: string;
};

export type TrashedCircuit = {
  id: string;
  code: number | null;
  name: string | null;
  status: SupplierStatus;
  deletedAt: string;
  deletedBy: DeletedBy;
  purgeAt: string;
};

export type SkippedTrashLink = {
  benefitId: string;
  benefitCode: number | null;
  benefitName: string | null;
  kind: 'STANDARD' | 'OPTIONAL' | 'ACCOMMODATION' | 'HOTEL_ASSOCIATION';
  reason:
    | 'STEP_DELETED'
    | 'HOTEL_DELETED'
    | 'STANDARD_TAKEN'
    | 'STANDARD_ACCOMMODATION_TAKEN';
  circuitCode: number | null;
  circuitName: string | null;
  hotelCode: number | null;
  hotelName: string | null;
};

const formatDeletedBy = (deletedBy: DeletedBy) =>
  deletedBy
    ? [deletedBy.firstname, deletedBy.lastname].filter(Boolean).join(' ') ||
      deletedBy.email
    : '-';

const selectColumn = <T,>(): ColumnDef<T> => ({
  id: 'select',
  header: ({ table }) => (
    <Checkbox
      checked={
        table.getIsAllPageRowsSelected() ||
        (table.getIsSomePageRowsSelected() && 'indeterminate')
      }
      onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
      aria-label="Select all"
    />
  ),
  cell: ({ row }) => (
    <Checkbox
      checked={row.getIsSelected()}
      onCheckedChange={(value) => row.toggleSelected(!!value)}
      aria-label="Select row"
    />
  ),
  enableSorting: false,
  enableHiding: false,
});

const deletionColumns = <
  T extends { deletedAt: string; deletedBy: DeletedBy; purgeAt: string },
>(
  t: Translator,
): ColumnDef<T>[] => [
  {
    accessorKey: 'deletedAt',
    header: t('trash.columns.deletedAt'),
    enableSorting: false,
    cell: ({ row }) => (
      <span>
        {format(new Date(row.original.deletedAt), 'dd/MM/yyyy HH:mm')}
      </span>
    ),
  },
  {
    accessorKey: 'deletedBy',
    header: t('trash.columns.deletedBy'),
    enableSorting: false,
    cell: ({ row }) => <span>{formatDeletedBy(row.original.deletedBy)}</span>,
  },
  {
    accessorKey: 'purgeAt',
    header: t('trash.columns.purgeAt'),
    enableSorting: false,
    cell: ({ row }) => {
      const daysLeft = differenceInCalendarDays(
        new Date(row.original.purgeAt),
        new Date(),
      );
      return (
        <span>
          {daysLeft > 0
            ? t('trash.purgeIn', { count: daysLeft })
            : t('trash.purgeToday')}
        </span>
      );
    },
  },
];

export const TRASHED_BENEFIT_COLUMNS = (
  t: Translator,
): ColumnDef<TrashedBenefit>[] => [
  selectColumn<TrashedBenefit>(),
  {
    accessorKey: 'code',
    header: t('trash.columns.code'),
    enableSorting: false,
    cell: ({ row }) => <span>{formatBenefitCode(row.original.code)}</span>,
  },
  {
    accessorKey: 'name',
    header: t('trash.columns.name'),
    enableSorting: false,
  },
  {
    accessorKey: 'type',
    header: t('trash.columns.type'),
    enableSorting: false,
    cell: ({ row }) => (
      <BenefitTypeCell
        type={
          row.original.type === 'ACTIVITY' && row.original.activity
            ? row.original.activity.activityType
            : row.original.type
        }
      />
    ),
  },
  {
    accessorKey: 'supplier.name',
    header: t('trash.columns.supplier'),
    enableSorting: false,
    cell: ({ row }) => <span>{row.original.supplier?.name ?? '-'}</span>,
  },
  ...deletionColumns<TrashedBenefit>(t),
];

export const TRASHED_CIRCUIT_COLUMNS = (
  t: Translator,
): ColumnDef<TrashedCircuit>[] => [
  selectColumn<TrashedCircuit>(),
  {
    accessorKey: 'code',
    header: t('trash.columns.code'),
    enableSorting: false,
    cell: ({ row }) => <span>{formatCircuitCode(row.original.code)}</span>,
  },
  {
    accessorKey: 'name',
    header: t('trash.columns.name'),
    enableSorting: false,
  },
  {
    accessorKey: 'status',
    header: t('trash.columns.status'),
    enableSorting: false,
    cell: ({ row }) => <SupplierStatusCell status={row.original.status} />,
  },
  ...deletionColumns<TrashedCircuit>(t),
];
